/**
 * Connexion.
 *
 * Le point delicat n'est pas le formulaire : c'est le MFA. Le backend
 * refuse la connexion d'un role sensible (owner, admin, security) tant
 * que la double authentification n'est pas activee. Afficher
 * « identifiants invalides » dans ce cas est LA principale source de
 * confusion a la premiere connexion (CDC §4.1) - l'utilisateur retape son
 * mot de passe indefiniment alors qu'il est correct.
 *
 * On distingue donc trois situations :
 *   - identifiants faux          -> le dire ;
 *   - code TOTP attendu          -> afficher le champ ;
 *   - MFA pas encore activee     -> GUIDER vers l'activation.
 */
import { useState } from "react";

import { ApiError, api } from "../api";
import { session } from "../session";
import { Erreur } from "../components/ui";
import { ActivationMfa } from "./ActivationMfa";

interface Props {
  onConnecte: () => void;
  onCreerCompte: () => void;
}

export function Login({ onConnecte, onCreerCompte }: Props) {
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  // 08/10/2026 : plus de numero de compte a saisir ; il n'est choisi que si
  // l'adresse est membre de plusieurs comptes (liste rendue par le serveur).
  const [comptes, setComptes] = useState<{ numero: number; nom: string }[] | null>(null);
  const [aucunCompte, setAucunCompte] = useState(false);
  const [totp, setTotp] = useState("");
  const [champTotp, setChampTotp] = useState(false);
  const [mfaAActiver, setMfaAActiver] = useState(false);
  const [activation, setActivation] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);
  const [reinitEnvoyee, setReinitEnvoyee] = useState(false);

  const connecter = async (numero?: number) => {
    setEnvoi(true);
    setErreur(null);
    setMfaAActiver(false);
    try {
      const choix = numero === undefined ? undefined : String(numero);
      const { access_token } = await api.login(email, motDePasse, choix, totp || undefined);
      session.ouvrir(access_token, choix ?? "");
      onConnecte();
    } catch (e2) {
      const err = e2 instanceof ApiError ? e2 : null;
      const liste = err?.corps?.comptes as { numero: number; nom: string }[] | undefined;
      if (err?.status === 409 && liste) {
        setComptes(liste);
        return;
      }
      if (err?.status === 409 && err.corps?.aucun_compte) {
        setAucunCompte(true);
        return;
      }
      if (err?.kind === "mfa_required") {
        // Le detail du serveur distingue "code attendu" de "MFA a
        // configurer". Sans cette nuance, on renverrait l'utilisateur
        // vers un champ qu'il ne peut pas remplir.
        const aConfigurer = /configur|activ|setup|obligatoire/i.test(err.detail ?? "");
        if (aConfigurer && !champTotp) setMfaAActiver(true);
        else setChampTotp(true);
      }
      setErreur(e2);
    } finally {
      setEnvoi(false);
    }
  };

  const soumettre = (e: React.FormEvent) => {
    e.preventDefault();
    void connecter();
  };

  const creerMonCompte = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await api.creerMonCompte(email, motDePasse, totp || undefined);
      session.ouvrir(r.access_token, String(r.numero));
      onConnecte();
    } catch (e2) {
      setErreur(e2);
    } finally {
      setEnvoi(false);
    }
  };

  const reinitialiser = async () => {
    // Le backend n'a besoin QUE de l'e-mail : exiger l'organisation
    // serait une barriere inventee, et elle bloque precisement la
    // personne qui ne connait pas encore son espace - le cas du premier
    // acces. Constate a l'ecran le 30/08/2026.
    if (!email.includes("@")) {
      setErreur(new ApiError("validation", 422, "Renseignez votre adresse e-mail."));
      return;
    }
    setErreur(null);
    try {
      await api.demanderReinitialisation(email);
    } catch {
      // Reponse volontairement identique en cas d'echec : confirmer
      // qu'une adresse existe permettrait d'enumerer les comptes.
    }
    setReinitEnvoyee(true);
  };

  if (comptes) {
    return (
      <div className="centered" style={{ padding: "2rem 1rem" }}>
        <div className="card stack" style={{ width: "min(28rem, 100%)" }}>
          <h1 className="card__title">Quel compte ouvrir ?</h1>
          <p className="muted" style={{ margin: 0 }}>Votre adresse est membre de plusieurs comptes.</p>
          {comptes.map((c) => (
            <button key={c.numero} type="button" className="btn btn--secondary" disabled={envoi}
                    onClick={() => void connecter(c.numero)}>
              {c.nom} — n° {c.numero}
            </button>
          ))}
          <Erreur erreur={erreur} />
          <button type="button" className="btn btn--link" onClick={() => setComptes(null)}>Retour</button>
        </div>
      </div>
    );
  }

  if (activation) {
    return (
      <ActivationMfa
        email={email}
        motDePasse={motDePasse}
        onActivee={() => { setActivation(false); setMfaAActiver(false); setChampTotp(true); setTotp(""); }}
        onAnnuler={() => setActivation(false)}
      />
    );
  }

  return (
    <div className="centered" style={{ padding: "2rem 1rem" }}>
      <form className="card stack" style={{ width: "min(28rem, 100%)" }} onSubmit={soumettre}>
        <div>
          <h1 className="card__title" style={{ marginBottom: "0.15rem" }}>
            Sign365
          </h1>
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Connexion à votre espace.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="email">
            Adresse e-mail
          </label>
          <input
            id="email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="mdp">
            Mot de passe
          </label>
          <input
            id="mdp"
            className="input"
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {champTotp && (
          <div className="field">
            <label className="field-label" htmlFor="totp">
              Code de double authentification
            </label>
            <input
              id="totp"
              className="input"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
            />
            <p className="muted" style={{ marginTop: "0.3rem" }}>
              Les six chiffres affichés par votre application d'authentification.
            </p>
          </div>
        )}

        {/* Le cas qui compte : le mot de passe est bon, mais le compte ne
            peut pas se connecter tant que la MFA n'est pas activee. */}
        {aucunCompte && (
          <div className="alert alert--info" role="status">
            <strong>Vous n'êtes membre d'aucun compte.</strong>
            <p style={{ margin: "0.5rem 0" }}>Vos identifiants sont corrects. Créez votre propre compte : vous en serez
              le propriétaire.</p>
            <button type="button" className="btn btn--primary" disabled={envoi} onClick={() => void creerMonCompte()}>
              Créer mon compte
            </button>
          </div>
        )}
        {mfaAActiver ? (
          <div className="alert alert--info" role="alert">
            <strong>Votre rôle exige la double authentification.</strong>
            <p style={{ margin: "0.5rem 0" }}>
              Vos identifiants sont corrects. Activez-la maintenant avec une application
              d'authentification sur votre téléphone : deux minutes.
            </p>
            <button type="button" className="btn btn--primary" onClick={() => setActivation(true)}>
              Activer la double authentification
            </button>
          </div>
        ) : (
          <Erreur erreur={erreur} />
        )}

        {reinitEnvoyee && (
          <div className="alert alert--info" role="status">
            Si un compte existe pour cette adresse, un lien de réinitialisation vient d'être
            envoyé.
          </div>
        )}

        <div className="actions" style={{ marginTop: 0 }}>
          <button type="submit" className="btn btn--primary" disabled={envoi}>
            Se connecter
          </button>
          <button type="button" className="btn btn--link" onClick={() => void reinitialiser()}>
            Mot de passe oublié
          </button>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          Pas encore de compte ?{" "}
          <button type="button" className="btn btn--link" onClick={onCreerCompte}>Créer un compte</button>
        </p>
      </form>
    </div>
  );
}
